import { lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import LandingHeader from "@/components/landing/LandingHeader";
import HeroSection from "@/components/landing/HeroSection";
import { useLazyPreload } from "@/hooks/useLazyPreload";

// Lazy load functions for below-the-fold components
const importWhoIsThisFor = () => import("@/components/landing/WhoIsThisFor");
const importAllFeaturesSection = () => import("@/components/landing/AllFeaturesSection");
const importCoreFeatures = () => import("@/components/landing/CoreFeatures");
const importWhyDifferent = () => import("@/components/landing/WhyDifferent");
const importPricingSection = () => import("@/components/landing/PricingSection");
const importCommissionSection = () => import("@/components/landing/CommissionSection");
const importTestimonialsSection = () => import("@/components/landing/TestimonialsSection");
const importLanguageSupport = () => import("@/components/landing/LanguageSupport");
const importSecuritySection = () => import("@/components/landing/SecuritySection");
const importHowItWorks = () => import("@/components/landing/HowItWorks");
const importFAQSection = () => import("@/components/landing/FAQSection");
const importFinalCTA = () => import("@/components/landing/FinalCTA");
const importFooter = () => import("@/components/landing/Footer");

// Lazy components
const WhoIsThisFor = lazy(importWhoIsThisFor);
const AllFeaturesSection = lazy(importAllFeaturesSection);
const CoreFeatures = lazy(importCoreFeatures);
const WhyDifferent = lazy(importWhyDifferent);
const PricingSection = lazy(importPricingSection);
const CommissionSection = lazy(importCommissionSection);
const TestimonialsSection = lazy(importTestimonialsSection);
const LanguageSupport = lazy(importLanguageSupport);
const SecuritySection = lazy(importSecuritySection);
const HowItWorks = lazy(importHowItWorks);
const FAQSection = lazy(importFAQSection);
const FinalCTA = lazy(importFinalCTA);
const Footer = lazy(importFooter);

// Components to preload
const lazyImports = [
  importWhoIsThisFor,
  importAllFeaturesSection,
  importCoreFeatures,
  importWhyDifferent,
  importPricingSection,
  importCommissionSection,
  importTestimonialsSection,
  importLanguageSupport,
  importSecuritySection,
  importHowItWorks,
  importFAQSection,
  importFinalCTA,
  importFooter,
];

const Index = () => {
  // Preload components when user scrolls near the fold
  useLazyPreload(lazyImports, "300px");

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "VLE Plus - CSC Billing Software",
    "alternateName": ["VLE Plus", "CSC Billing Software", "Maha e-Seva Kendra Software", "VLE Software"],
    "applicationCategory": "BusinessApplication",
    "applicationSubCategory": "Billing Software",
    "operatingSystem": "Web, Android, iOS",
    "description": "VLE Plus is India's #1 free billing software for CSC, Maha e-Seva Kendra & Digital Seva operators. Best CSC login software with features for VLE registration, kiosk banking, AEPS tracking, PMFBY, Pik Vima, Setu management, WhatsApp invoice & VLE referral program. Perfect for sarkari CSC and sarkari VLE.",
    "url": "https://vleplus.in",
    "image": "https://vleplus.in/og-vleplus.png",
    "screenshot": "https://vleplus.in/og-vleplus.png",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "2999",
      "priceCurrency": "INR",
      "availability": "https://schema.org/InStock",
      "offerCount": "5",
      "description": "Free trial available. Plans start from ₹199/month for CSC VLEs and Maha e-Seva Kendra operators."
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "CSC VLE, CSC Registration Users, VLE Login Users, Maha e-Seva Kendra Owners, Maha e-Seva Kendra Near Me, Digital Seva Operators, Kiosk Banking Operators, AEPS Operators, Pik Vima Agents, PMFBY CSC Agents, Sarkari CSC, Sarkari VLE"
    },
    "featureList": [
      "CSC billing software",
      "CSC login integration",
      "VLE registration support",
      "VLE ID and VLE code management",
      "Maha e-Seva Kendra billing",
      "Maha e-Seva Kendra login",
      "Free billing software trial",
      "Billing software for small business",
      "Customer management system",
      "WhatsApp invoice sharing",
      "Kiosk banking AEPS tracking",
      "CSC UTI PAN services",
      "PMFBY CSC integration",
      "PM Kisan CSC support",
      "Pik Vima crop insurance management",
      "Setu application tracking",
      "Daily book accounting",
      "Due & credit tracking",
      "PDF & Excel reports",
      "Multi-language support (Hindi, Marathi, English)",
      "VLE referral program with commission",
      "Mobile-friendly PWA",
      "CSC Safar compatible",
      "CSC Portal integration ready"
    ],
    "creator": {
      "@type": "Organization",
      "name": "VLE Plus",
      "url": "https://vleplus.in"
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "ratingCount": "1250",
      "bestRating": "5",
      "worstRating": "1"
    },
    "inLanguage": ["en", "hi", "mr"],
    "keywords": "csc, csc login, csc registration, vle, vle login, maha e seva kendra, billing software, free billing software"
  };

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "VLE Plus",
    "alternateName": "VLE Plus Billing Software",
    "url": "https://vleplus.in",
    "logo": "https://vleplus.in/icons/icon-512x512.png",
    "description": "India's leading billing and management software for CSC VLEs, Maha e-Seva Kendras, and Digital Seva operators",
    "sameAs": [],
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "availableLanguage": ["English", "Hindi", "Marathi"]
    },
    "areaServed": {
      "@type": "Country",
      "name": "India"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [{
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://vleplus.in/"
    }]
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is the best CSC billing software in India?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "VLE Plus is India's #1 CSC billing software, designed specifically for CSC VLEs, Maha e-Seva Kendras, and Digital Seva operators. It includes smart billing, customer management, kiosk banking, AEPS tracking, Pik Vima, PMFBY, and WhatsApp invoice features."
        }
      },
      {
        "@type": "Question",
        "name": "How to do CSC registration and get VLE ID?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "For CSC registration, visit the official CSC portal. Once you have your VLE ID and VLE code, you can use VLE Plus billing software to manage your CSC center's billing, customers, and daily accounts professionally."
        }
      },
      {
        "@type": "Question",
        "name": "Is there free billing software for Maha e-Seva Kendra?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "VLE Plus offers a 7-day free trial for Maha e-Seva Kendra operators. It's the best billing software with features like maha e seva kendra login integration, customer management, and multi-language support in Marathi, Hindi, and English."
        }
      },
      {
        "@type": "Question",
        "name": "Does VLE Plus support Kiosk Banking, AEPS and PMFBY CSC?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! VLE Plus has dedicated modules for Kiosk Banking (AEPS cash withdrawal/deposit tracking), Pik Vima (PMFBY CSC) crop insurance management, and commission calculations with slab-based management for all CSC services."
        }
      },
      {
        "@type": "Question",
        "name": "How to find Maha e-Seva Kendra near me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "VLE Plus has a 'Find CSC' feature where citizens can locate nearby Maha e-Seva Kendras, CSC centers, and Digital Seva Kendras. VLEs registered on VLE Plus get a public profile visible in search results."
        }
      },
      {
        "@type": "Question",
        "name": "Can VLE earn money by referring other CSC VLEs?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! VLE Plus has a referral program where CSC VLEs and Maha e-Seva Kendra owners can earn up to 25% commission on every subscription. This is a great way to earn passive income from your VLE network."
        }
      },
      {
        "@type": "Question",
        "name": "What is VLE Plus used for in CSC centers?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "VLE Plus is used for CSC billing, customer management, daily book accounting, kiosk banking tracking, Pik Vima management, Setu case tracking, WhatsApp invoicing, due/credit tracking, and generating PDF/Excel reports for CSC and Maha e-Seva Kendra operations."
        }
      }
    ]
  };

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>VLE Plus – #1 CSC Billing Software | Maha e-Seva Kendra | Free VLE Software India</title>
        <meta name="description" content="VLE Plus - India's best free billing software for CSC, Maha e-Seva Kendra & Digital Seva. CSC login, VLE registration, kiosk banking, AEPS, PMFBY, Pik Vima, WhatsApp invoice. Used by 1000+ VLEs. Start free trial!" />
        <meta name="keywords" content="csc, csc login, csc registration, csc billing software, vle, vle login, vle registration, csc vle, vle id, vle code, maha e seva kendra, maha e seva kendra near me, maha e seva kendra login, maha e seva kendra registration, billing software, free billing software, billing software for small business, csc portal, csc safar, csc uti, csc pan, pmfby csc, pmfby csc login, pm kisan csc login, vle mahaonline, vle bazaar, sarkari csc, sarkari vle, kiosk banking software, aeps software, pik vima software, digital seva billing, csc center software, csc certificate download, csc job portal, csc sarkari" />
        <link rel="canonical" href="https://vleplus.in/" />
        
        {/* Open Graph */}
        <meta property="og:title" content="VLE Plus – #1 CSC Billing Software | Maha e-Seva Kendra | Free VLE Software" />
        <meta property="og:description" content="Best free billing software for CSC, Maha e-Seva Kendra. CSC login, VLE registration, kiosk banking, AEPS, Pik Vima, WhatsApp invoice. Start free trial!" />
        <meta property="og:url" content="https://vleplus.in/" />
        <meta property="og:site_name" content="VLE Plus" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://vleplus.in/og-vleplus.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:locale" content="en_IN" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="VLE Plus – #1 CSC Billing Software | Maha e-Seva Kendra" />
        <meta name="twitter:description" content="Best free billing software for CSC VLE & Maha e-Seva Kendras. Kiosk banking, AEPS, Pik Vima, PMFBY. Start free trial!" />
        <meta name="twitter:image" content="https://vleplus.in/og-vleplus.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="googlebot" content="index, follow" />
        <meta name="bingbot" content="index, follow" />
        <meta name="author" content="VLE Plus" />
        <meta name="geo.region" content="IN" />
        <meta name="geo.placename" content="India" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="min-h-screen bg-background">
        <LandingHeader />
        <main>
          <HeroSection />
          <Suspense fallback={<div className="min-h-[200px]" />}>
            {/* Who Is This For */}
            <section id="audience">
              <WhoIsThisFor />
            </section>
            
            {/* All Features - Comprehensive */}
            <section id="all-features">
              <AllFeaturesSection />
            </section>
            
            {/* Quick Feature Highlights */}
            <section id="features">
              <CoreFeatures />
            </section>
            
            {/* Why Different */}
            <WhyDifferent />
            
            {/* Pricing Table */}
            <section id="pricing">
              <PricingSection />
            </section>
            
            {/* Referral & Commission */}
            <section id="referral">
              <CommissionSection />
            </section>
            
            {/* Testimonials & Stats */}
            <TestimonialsSection />
            
            {/* Language Support */}
            <LanguageSupport />
            
            {/* Security */}
            <SecuritySection />
            
            {/* How It Works */}
            <section id="how-it-works">
              <HowItWorks />
            </section>
            
            {/* FAQ */}
            <section id="faq">
              <FAQSection />
            </section>
            
            {/* Final CTA */}
            <FinalCTA />
          </Suspense>
        </main>
        <Suspense fallback={null}>
          <Footer />
        </Suspense>
      </div>
    </>
  );
};

export default Index;